import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Brain, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

// iter130 Phase A — /auth/magic?t=<token>
// Landing page for magic-link sign-ins. Consumes the token on mount
// and either lands the user in /dashboard or shows a friendly error.
export default function MagicLinkConsume() {
  const { consumeMagicLink } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState("consuming"); // consuming | error
  const [error, setError] = useState("");
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;    // React StrictMode double-invoke guard
    attempted.current = true;
    const token = params.get("t");
    if (!token) {
      setState("error");
      setError("This sign-in link is missing its token. Please request a new one from the login page.");
      return;
    }
    (async () => {
      const res = await consumeMagicLink(token);
      if (res.ok) {
        toast.success(`Welcome, ${res.user.name || res.user.email}`);
        nav("/dashboard");
      } else {
        setState("error");
        setError(res.error || "This sign-in link is invalid or has expired.");
      }
    })();
  }, [params, consumeMagicLink, nav]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--bt-bg)]">
      <div className="w-full max-w-md space-y-5 bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-8 text-center">
        <div className="flex items-center justify-center gap-3">
          <span className="w-8 h-8 rounded-sm bg-[var(--bt-accent)] flex items-center justify-center">
            <Brain className="w-4 h-4 text-black" strokeWidth={2.5} />
          </span>
          <span className="font-heading text-lg">Brain<span className="text-[var(--bt-accent)]">TRADES</span></span>
        </div>

        {state === "consuming" && (
          <div className="py-6 space-y-3" data-testid="magic-consuming">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-[var(--bt-accent)]" />
            <p className="text-[var(--bt-text-muted)]">Signing you in…</p>
          </div>
        )}

        {state === "error" && (
          <div className="space-y-4" data-testid="magic-error">
            <h1 className="font-heading text-xl">Couldn&apos;t sign you in</h1>
            <p className="text-sm text-[var(--bt-text-muted)] leading-relaxed">
              {error}
            </p>
            <Link
              to="/login"
              data-testid="magic-back-to-login"
              className="inline-flex items-center justify-center gap-1 h-11 px-5 rounded-sm bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold text-sm"
            >
              Back to sign in <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
